/**
 * Bugün's business tiles (panel audit 25 Oct 2026, F2): revenue and orders this month against last month
 * to the same day, entries from outside to the site over seven days and outreach answers over seven days.
 * Each tile is one KpiTile: the value, its change in words and colour (never the accent, which means
 * "needs you"), what it is compared with, a fourteen-day sparkline and the page behind it. The phone shows
 * them as a 2x2 grid under the inbox headline; the desktop uses the same grid beside it.
 */

import type { Pulse, Snapshot } from '../api/types.js';
import { Link } from '../app/router.js';
import { formatIsoDay, formatNumber, formatUsd } from '../lib/format.js';

export type DeltaTone = 'up' | 'down' | 'flat';

export interface Delta {
  /** "↑ %19", "↓ 2", "aynı"; empty when there is nothing to compare with. */
  text: string;
  /** The same in words for screen readers. */
  spoken: string;
  tone: DeltaTone;
}

/**
 * The change from `before` to `now`: a whole percent for money and visits, a plain difference for small
 * counts (a percent of 1 against 3 says more than it knows).
 */
export function deltaOf(now: number, before: number, mode: 'pct' | 'count'): Delta {
  if (now === before) return { text: 'aynı', spoken: 'değişmedi', tone: 'flat' };
  const tone: DeltaTone = now > before ? 'up' : 'down';
  const arrow = tone === 'up' ? '↑' : '↓';
  const verb = tone === 'up' ? 'fazla' : 'az';
  if (mode === 'count' || before === 0) {
    if (before === 0 && mode === 'pct') return { text: 'yeni', spoken: 'önceki dönemde yoktu', tone };
    const diff = Math.abs(now - before);
    return { text: `${arrow} ${formatNumber(diff)}`, spoken: `${formatNumber(diff)} ${verb}`, tone };
  }
  const pct = Math.round((Math.abs(now - before) / before) * 100);
  return { text: `${arrow} %${formatNumber(pct)}`, spoken: `yüzde ${pct} ${verb}`, tone };
}

/** Fourteen days as small bars in the neutral data colour, the last one strong. Decoration: the tile says it. */
export function Sparkline({ values }: { values: readonly number[] }) {
  if (values.length === 0) return <span className="spark spark--empty" aria-hidden="true" />;
  const width = 140;
  const height = 24;
  const gap = 2;
  const bar = (width - gap * (values.length - 1)) / values.length;
  const max = Math.max(1, ...values);
  return (
    <svg
      className="spark"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      {values.map((value, index) => {
        const h = value === 0 ? 1 : Math.max(2, (value / max) * height);
        return (
          <rect
            // biome-ignore lint/suspicious/noArrayIndexKey: one bar per day position, the list never reorders.
            key={index}
            className={index === values.length - 1 ? 'spark__bar spark__bar--last' : 'spark__bar'}
            x={index * (bar + gap)}
            y={height - h}
            width={bar}
            height={h}
          />
        );
      })}
    </svg>
  );
}

export function KpiTile({
  label,
  value,
  unit,
  delta,
  base,
  spark,
  source,
  to,
  page,
}: {
  label: string;
  value: string;
  unit?: string;
  delta: Delta | null;
  base: string;
  spark: readonly number[];
  source: string;
  to: string;
  /** The page the tile opens, for the label: "Satışlar". */
  page: string;
}) {
  const spoken = [
    `${label}: ${value}${unit ? ` ${unit}` : ''}`,
    delta === null ? base : `${base}, ${delta.spoken}`,
    source,
    `${page} ekranını aç`,
  ].join('. ');
  return (
    <Link to={to} className="kpi-tile" aria-label={spoken}>
      <span className="kpi-tile__label">{label}</span>
      <span className="kpi-tile__value">
        <span className="mono">{value}</span>
        {unit ? <span className="kpi-tile__unit"> {unit}</span> : null}
      </span>
      <span className="kpi-tile__compare">
        {delta === null ? null : <span className={`kpi-tile__delta kpi-tile__delta--${delta.tone}`}>{delta.text}</span>}
        <span className="kpi-tile__base">{base}</span>
      </span>
      <Sparkline values={spark} />
      <span className="kpi-tile__source">{source}</span>
    </Link>
  );
}

/** "Ekim 2026" from the store summary is "Ekim" on a tile. */
function monthWord(label: string): string {
  return label.split(' ')[0] ?? label;
}

function visitsTile(pulse: Pulse) {
  const visits = pulse.visits;
  if (visits.through === null) {
    return (
      <KpiTile
        label="Site ziyareti · 7 gün"
        value="-"
        delta={null}
        base="Sunucu kaydı henüz okunmadı"
        spark={[]}
        source="dışarıdan giriş"
        to="/site"
        page="Site"
      />
    );
  }
  return (
    <KpiTile
      label="Site ziyareti · 7 gün"
      value={formatNumber(visits.last7)}
      delta={deltaOf(visits.last7, visits.previous7, 'pct')}
      base={`önceki 7 gün ${formatNumber(visits.previous7)}`}
      spark={visits.daily}
      source={`dışarıdan giriş · son gün ${formatIsoDay(visits.through)}`}
      to="/site"
      page="Site"
    />
  );
}

/** The four tiles in a 2x2 grid; nothing when the API does not send them yet. */
export function PulseTiles({ snapshot, desktop = false }: { snapshot: Snapshot; desktop?: boolean }) {
  const pulse = snapshot.pulse;
  if (pulse === null) return null;
  const month = monthWord(snapshot.store.monthLabel);
  const outreach = pulse.outreach;
  return (
    <section className={desktop ? 'pulse pulse--desk' : 'pulse'} aria-label="İşin nabzı">
      <KpiTile
        label={`Ciro · ${month}`}
        value={formatUsd(pulse.revenue.monthToDate, 0)}
        delta={deltaOf(pulse.revenue.monthToDate, pulse.revenue.lastMonthToDate, 'pct')}
        base={`geçen ay bu güne kadar ${formatUsd(pulse.revenue.lastMonthToDate, 0)}`}
        spark={pulse.revenue.daily}
        source="Etsy ve ödenen işler · 14 gün"
        to="/satislar"
        page="Satışlar"
      />
      <KpiTile
        label={`Sipariş · ${month}`}
        value={formatNumber(pulse.orders.monthToDate)}
        delta={deltaOf(pulse.orders.monthToDate, pulse.orders.lastMonthToDate, 'count')}
        base={`geçen ay bu güne kadar ${formatNumber(pulse.orders.lastMonthToDate)}`}
        spark={pulse.orders.daily}
        source="Etsy siparişleri · 14 gün"
        to="/satislar"
        page="Satışlar"
      />
      {visitsTile(pulse)}
      <KpiTile
        label="Tanıtım · 7 gün"
        value={formatNumber(outreach.replied7)}
        unit="cevap"
        delta={deltaOf(outreach.replied7, outreach.previousReplied7, 'count')}
        base={`önceki 7 gün ${formatNumber(outreach.previousReplied7)}`}
        spark={outreach.daily}
        source={`${formatNumber(outreach.interested7)} ilgili · ${formatNumber(outreach.sent7)} mail gitti`}
        to="/tanitim-takibi"
        page="Tanıtım takibi"
      />
    </section>
  );
}

/** "Dün 6 kart süresi doldu": silence on a card shows on Bugün (audit F4). */
export function ExpiredLine({ snapshot }: { snapshot: Snapshot }) {
  const expired = snapshot.pulse?.expiredYesterday ?? 0;
  if (expired === 0) return null;
  return (
    <p className="expired-line" role="note">
      Dün <span className="mono">{expired}</span> kart süresi doldu; karar verilmeden kapandı.
    </p>
  );
}
